import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { FIELD } from "@/constants/testIds";
import { Circle, Type, Undo2, Trash2, Check, X } from "lucide-react";

const COLORS = ["#E11D48", "#F59E0B", "#FFFFFF", "#27523C"];
const MAX_W = 1280;

export function PhotoAnnotator({ src, onSave, onSkip, suggestions }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const startRef = useRef(null);
  const seededRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [shapes, setShapes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [tool, setTool] = useState("circle");
  const [color, setColor] = useState("#E11D48");
  const [label, setLabel] = useState("");

  // load image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      const w = Math.min(img.naturalWidth || MAX_W, MAX_W);
      const scale = w / (img.naturalWidth || w);
      canvas.width = w;
      canvas.height = Math.round((img.naturalHeight || w) * scale);
      setLoaded(true);
    };
    img.src = src;
  }, [src]);

  const lineW = () => Math.max(3, (canvasRef.current?.width || 600) / 160);
  const fontPx = () => Math.max(16, Math.round((canvasRef.current?.width || 600) / 26));

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    const all = preview ? [...shapes, preview] : shapes;
    for (const s of all) {
      if (s.type === "circle") {
        ctx.beginPath();
        ctx.lineWidth = lineW();
        ctx.strokeStyle = s.color;
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        ctx.shadowBlur = lineW();
        ctx.arc(s.x, s.y, Math.max(6, s.r), 0, Math.PI * 2);
        ctx.stroke();
        ctx.shadowBlur = 0;
      } else if (s.type === "label") {
        const fp = fontPx();
        ctx.font = `700 ${fp}px Outfit, system-ui, sans-serif`;
        const padX = fp * 0.45, padY = fp * 0.3;
        const tw = ctx.measureText(s.text).width;
        const bx = s.x, by = s.y;
        ctx.fillStyle = s.color;
        ctx.strokeStyle = "rgba(0,0,0,0.25)";
        const bw = tw + padX * 2, bh = fp + padY * 2;
        const rr = fp * 0.35;
        ctx.beginPath();
        ctx.moveTo(bx + rr, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + bh, rr);
        ctx.arcTo(bx + bw, by + bh, bx, by + bh, rr);
        ctx.arcTo(bx, by + bh, bx, by, rr);
        ctx.arcTo(bx, by, bx + bw, by, rr);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = s.color === "#FFFFFF" ? "#162920" : "#FFFFFF";
        ctx.textBaseline = "top";
        ctx.fillText(s.text, bx + padX, by + padY);
      }
    }
  }, [shapes, preview]);

  useEffect(() => { if (loaded) redraw(); }, [loaded, redraw]);

  // Pre-draw AI-suggested markers once the image is loaded (worker can edit/clear).
  useEffect(() => {
    if (!loaded || seededRef.current || !suggestions?.length) return;
    seededRef.current = true;
    const canvas = canvasRef.current;
    const w = canvas.width, h = canvas.height;
    const seed = [];
    for (const s of suggestions) {
      const cx = (s.x ?? 0.5) * w;
      const cy = (s.y ?? 0.5) * h;
      const r = Math.max(10, (s.r ?? 0.12) * w);
      seed.push({ type: "circle", x: cx, y: cy, r, color: "#E11D48" });
      if (s.label) {
        seed.push({ type: "label", x: cx, y: Math.max(0, cy - r - fontPx() - 6), text: String(s.label).slice(0, 40), color: "#E11D48" });
      }
    }
    setShapes(seed);
  }, [loaded, suggestions]);

  const toCanvas = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const onDown = (e) => {
    if (!loaded) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = toCanvas(e);
    if (tool === "circle") {
      startRef.current = p;
      setPreview({ type: "circle", x: p.x, y: p.y, r: 0, color });
    } else {
      setShapes((s) => [...s, { type: "label", x: p.x, y: p.y, text: (label || "Here").slice(0, 40), color }]);
    }
  };
  const onMove = (e) => {
    if (tool !== "circle" || !startRef.current) return;
    const p = toCanvas(e);
    const r = Math.hypot(p.x - startRef.current.x, p.y - startRef.current.y);
    setPreview({ type: "circle", x: startRef.current.x, y: startRef.current.y, r, color });
  };
  const onUp = () => {
    if (tool === "circle" && preview && preview.r > 3) setShapes((s) => [...s, preview]);
    startRef.current = null;
    setPreview(null);
  };

  const save = () => {
    const prev = preview; setPreview(null);
    requestAnimationFrame(() => {
      redraw();
      const url = canvasRef.current.toDataURL("image/jpeg", 0.85);
      onSave(url);
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border p-0.5">
          <button type="button" onClick={() => setTool("circle")} className={cn("flex items-center gap-1 rounded px-2.5 py-1.5 text-sm font-medium", tool === "circle" ? "bg-forest-mid text-white" : "text-slate-600")}>
            <Circle className="h-4 w-4" /> Circle
          </button>
          <button type="button" onClick={() => setTool("label")} className={cn("flex items-center gap-1 rounded px-2.5 py-1.5 text-sm font-medium", tool === "label" ? "bg-forest-mid text-white" : "text-slate-600")}>
            <Type className="h-4 w-4" /> Label
          </button>
        </div>
        <div className="flex items-center gap-1">
          {COLORS.map((c) => (
            <button key={c} type="button" onClick={() => setColor(c)}
              className={cn("h-7 w-7 rounded-full border-2", color === c ? "border-forest-dark ring-2 ring-forest-mid/30" : "border-white shadow")}
              style={{ backgroundColor: c }} />
          ))}
        </div>
      </div>

      {tool === "label" && (
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Type a label, then tap the photo to place it" className="h-10" />
      )}

      <div className="overflow-hidden rounded-xl border bg-slate-900">
        <canvas
          ref={canvasRef}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          className="block w-full touch-none"
          style={{ touchAction: "none", cursor: "crosshair" }}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length}>
            <Undo2 className="mr-1 h-4 w-4" /> Undo
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setShapes([])} disabled={!shapes.length}>
            <Trash2 className="mr-1 h-4 w-4" /> Clear
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{tool === "circle" ? "Drag on the photo to circle a spot" : "Tap the photo to drop a label"}</p>
      </div>

      <div className="flex gap-2 border-t pt-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onSkip} data-testid={FIELD.annotateSkip}>
          <X className="mr-1.5 h-4 w-4" /> Use original
        </Button>
        <Button type="button" className="flex-1 bg-forest-mid hover:bg-forest-dark" onClick={save} disabled={!shapes.length} data-testid={FIELD.annotateSave}>
          <Check className="mr-1.5 h-4 w-4" /> Save markup
        </Button>
      </div>
    </div>
  );
}
